/**
 * Optimized & GDPR Compliant Cookie Banner
 * 1. Uses Event Delegation for performance.
 * 2. defers logic to connectedCallback to prevent layout thrashing.
 * 3. Integrates with Shopify's Consent API for legal compliance.
 */
class CookieBanner extends ModalElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    
    // Performance: Initialize only when visible or on interaction to save main-thread work
    if (!theme.config.isTouch || Shopify.designMode) {
      this.init();
    } else {
      new theme.initWhenVisible(() => this.init());
    }
  }

  get settings() {
    return {
      testMode: this.getAttribute('data-test-mode') === 'true',
      delay: parseInt(this.getAttribute('data-delay') || 2, 10),
    };
  }

  init() {
    this.initialized = true;

    if (this.settings.testMode) {
      this.load(this.settings.delay);
      return;
    }

    // GDPR: Always load the official Shopify Consent API first
    window.Shopify.loadFeatures([{
      name: 'consent-tracking-api',
      version: '0.1',
      onLoad: () => this.checkConsentStatus()
    }]);
  }

  checkConsentStatus() {
    const cp = window.Shopify.customerPrivacy;
    
    /**
     * GDPR Logic: 
     * Only show if the user is in a region requiring consent (userCanBeTracked === false)
     * AND they haven't made a choice yet (no_interaction).
     */
    const needsConsent = !cp.userCanBeTracked();
    const noChoiceMade = cp.getTrackingConsent() === 'no_interaction';

    if (needsConsent && noChoiceMade) {
      this.load(this.settings.delay);
    }
  }

  load(delay) {
    if (Shopify.designMode) return;

    // Use requestIdleCallback to prevent jank during page load
    const scheduleShow = () => {
      setTimeout(() => this.show(), delay * 1000);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(scheduleShow);
    } else {
      scheduleShow();
    }

    // Optimized: Single event listener for all actions (Event Delegation)
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;

      if (btn.name === 'accept') this.handleConsent(true);
      if (btn.name === 'decline') this.handleConsent(false);
    });
  }

  handleConsent(isAccepted) {
    this.hide();
    
    // GDPR Requirement: Explicitly set consent via the official API
    // This tells Shopify (and integrated apps) whether they are allowed to track.
    window.Shopify.customerPrivacy?.setTrackingConsent(isAccepted, () => {
      const eventName = isAccepted ? 'trackingConsentAccepted' : 'trackingConsentDeclined';
      document.dispatchEvent(new CustomEvent(eventName));
    });
  }

  afterHide() {
    super.afterHide?.();
    document.body.classList.remove('has-cookie-banner');
  }

  afterShow() {
    super.afterShow?.();
    document.body.classList.add('has-cookie-banner');
  }
}

if (!customElements.get('cookie-banner')) {
  customElements.define('cookie-banner', CookieBanner);
}